import { StatusBadge, money } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Download, Zap, Receipt, Send, PauseCircle, AlertTriangle, Landmark, Hand } from "lucide-react";

// Payouts queue + auto-payout & pricing settings. State/handlers come from AdminDashboard.
export const PayoutsTab = ({
  payouts, payoutSettings, setPayoutSettings, pricingSettings, pricingForm, setPricingForm,
  savingPricing, exportCsv, toggleAutoPayout, saveAutoPayoutMax, savePricing, payout, holdPayout,
  providerInfo, setPayoutProvider,
}) => (
  <>
    <div className="mb-4 flex justify-end">
      <Button variant="ghost" size="sm" data-testid="export-payouts-btn" onClick={() => exportCsv("/admin/payouts/export", "bogoshare_payouts.csv")} className="text-muted-foreground">
        <Download className="mr-1 h-4 w-4" /> Export payouts
      </Button>
    </div>
    {providerInfo && (
      <div data-testid="payout-rail-settings" className="mb-5 rounded-xl border border-black/10 bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-foreground text-background"><Landmark className="h-4.5 w-4.5" /></div>
          <div className="flex-1">
            <p className="font-heading text-base" style={{ fontWeight: 700 }}>Payout rail</p>
            <p className="text-xs text-muted-foreground">
              Choose how outbound Interac e-Transfers are sent. Switch to Zum Rails any time — it takes effect immediately.
            </p>
            <div className="mt-3 inline-flex rounded-full border border-black/10 p-0.5" data-testid="payout-rail-toggle">
              <button type="button" data-testid="rail-manual-btn" onClick={() => setPayoutProvider("manual")}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  providerInfo.provider === "manual" ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"}`}>
                <Hand className="h-3.5 w-3.5" /> Manual e-Transfer
              </button>
              <button type="button" data-testid="rail-zum-btn" onClick={() => setPayoutProvider("zum")}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  providerInfo.provider === "zum" ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"}`}>
                <Zap className="h-3.5 w-3.5" /> Zum Rails
              </button>
            </div>
            <p className="mt-2 text-[11px]" data-testid="rail-status">
              {providerInfo.provider === "manual" ? (
                <span className="text-muted-foreground">Manual mode — withdrawals wait in the queue for you to send by hand, then mark released.</span>
              ) : providerInfo.zum_configured ? (
                <span className="text-green-700 font-medium">Zum Rails is live — automatic transfers will be sent through Zum.</span>
              ) : (
                <span className="text-amber-700 font-medium">Zum selected, but credentials aren't set yet — transfers are simulated until you add the ZUM_* keys.</span>
              )}
            </p>
          </div>
        </div>
      </div>
    )}

    {payoutSettings && (
      <div data-testid="auto-payout-settings" className="mb-5 rounded-xl border border-black/10 bg-white p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Zap className="h-4.5 w-4.5" /></div>
            <div>
              <p className="font-heading text-base" style={{ fontWeight: 700 }}>Automatic payouts</p>
              <p className="text-xs text-muted-foreground">
                Fees ≤ ${payoutSettings.max_fee} auto-credit the buyer's wallet ~{payoutSettings.release_hours}h after the fee settles. Larger amounts always wait for manual review.
              </p>
            </div>
          </div>
          <Switch
            data-testid="auto-payout-toggle"
            checked={!!payoutSettings.auto_payout_enabled}
            onCheckedChange={toggleAutoPayout}
          />
        </div>
        <div className="mt-4 flex items-end gap-3 border-t border-black/5 pt-4">
          <div>
            <Label className="text-xs">Auto-payout limit ($)</Label>
            <Input
              data-testid="auto-payout-max-input"
              type="number" min="0" step="5"
              value={payoutSettings.max_fee ?? ""}
              onChange={(e) => setPayoutSettings((prev) => ({ ...prev, max_fee: e.target.value }))}
              className="mt-1 w-40"
            />
          </div>
          <Button data-testid="auto-payout-max-save" onClick={saveAutoPayoutMax} className="rounded-full">Save limit</Button>
          <p className="pb-2 text-[11px] text-muted-foreground">Payouts at or below this amount pay out automatically; anything higher waits for your manual approval.</p>
        </div>
      </div>
    )}
    {pricingSettings && (
      <div data-testid="pricing-settings" className="mb-5 rounded-xl border border-black/10 bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent"><Receipt className="h-4.5 w-4.5" /></div>
          <div className="flex-1">
            <p className="font-heading text-base" style={{ fontWeight: 700 }}>BOGO payment scheme</p>
            <p className="text-xs text-muted-foreground">
              Partner pays: ½ cost of goods + tax (by province) + admin fee. Card processing is covered by BogoShare (Helcim deducts it from the deposit). The buyer receives ½ cost + tax; the app keeps the admin fee. <span className="font-medium">Multi-pack:</span> a flat fee is charged to both users (owner's is deducted from their payout).
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Admin fee (%)</Label>
                <Input data-testid="pricing-admin-fee-input" type="number" min="0" step="0.5"
                  value={pricingForm.admin_fee_pct}
                  onChange={(e) => setPricingForm((p) => ({ ...p, admin_fee_pct: e.target.value }))}
                  className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Est. processing (%)</Label>
                <Input data-testid="pricing-processing-input" type="number" min="0" step="0.1"
                  value={pricingForm.processing_estimate_pct}
                  onChange={(e) => setPricingForm((p) => ({ ...p, processing_estimate_pct: e.target.value }))}
                  className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Multi-pack flat fee ($/side)</Label>
                <Input data-testid="pricing-multipack-fee-input" type="number" min="0" step="0.5"
                  value={pricingForm.multipack_flat_fee}
                  onChange={(e) => setPricingForm((p) => ({ ...p, multipack_flat_fee: e.target.value }))}
                  className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Default province (canada-wide deals)</Label>
                <Input data-testid="pricing-province-input" maxLength={2}
                  value={pricingForm.default_province}
                  onChange={(e) => setPricingForm((p) => ({ ...p, default_province: e.target.value.toUpperCase() }))}
                  className="mt-1 uppercase" />
              </div>
            </div>
            <Button data-testid="save-pricing-btn" onClick={savePricing} disabled={savingPricing}
              className="mt-3 rounded-full" size="sm">
              {savingPricing ? "Saving…" : "Save pricing settings"}
            </Button>
          </div>
        </div>
      </div>
    )}
    {payouts.length === 0 ? (
      <p className="text-muted-foreground">No payouts to process.</p>
    ) : (
      <div className="space-y-3">
        {payouts.map((m) => (
          <div key={m.match_id} data-testid={`payout-${m.match_id}`} className="flex items-center gap-4 rounded-xl border border-black/10 bg-white p-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <StatusBadge status={m.status} />
                <span data-testid={`payout-status-${m.match_id}`} className="text-sm text-muted-foreground">payout {m.payout_status}</span>
              </div>
              <h3 className="mt-1 font-heading text-lg truncate" style={{ fontWeight: 600 }}>{m.deal?.title}</h3>
              <p className="text-sm text-muted-foreground">
                Pay <b>{m.primary_name}</b> {money(m.agreed_fee)} · from {m.partner_name}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
                  m.payment_status === "captured" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                }`} data-testid={`payment-status-${m.match_id}`}>
                  {m.payment_status === "captured" ? "Card captured" : `Card ${m.payment_status || "n/a"}`}
                </span>
                {m.payout_status === "auto_queued" && m.payout_release_at && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 font-semibold" data-testid={`auto-release-${m.match_id}`}>
                    <Zap className="h-3 w-3" /> Auto-release {new Date(m.payout_release_at).toLocaleString()}
                  </span>
                )}
                {m.payout_status === "failed" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 font-semibold" data-testid={`payout-failed-${m.match_id}`}>
                    <AlertTriangle className="h-3 w-3" /> {m.payout_error || "failed"}
                  </span>
                )}
                {m.payout_destination && (
                  <span className="text-muted-foreground" data-testid={`payout-dest-${m.match_id}`}>→ {m.payout_destination}</span>
                )}
                {m.helcim_capture_id ? (
                  <span className="font-mono-code text-muted-foreground" data-testid={`helcim-capture-${m.match_id}`}>
                    Helcim capture #{m.helcim_capture_id}
                  </span>
                ) : m.helcim_transaction_id ? (
                  <span className="font-mono-code text-muted-foreground" data-testid={`helcim-preauth-${m.match_id}`}>
                    Helcim pre-auth #{m.helcim_transaction_id}
                  </span>
                ) : (
                  <span className="text-muted-foreground">No card txn (mock/legacy)</span>
                )}
              </div>
            </div>
            {m.payout_status === "transferred" ? (
              <Badge className="bg-green-100 text-green-800 border-none shrink-0">Transferred</Badge>
            ) : (
              <div className="flex flex-col gap-2 shrink-0">
                <Button size="sm" data-testid={`release-payout-${m.match_id}`} onClick={() => payout(m.match_id)} className="rounded-full">
                  <Send className="mr-1 h-3.5 w-3.5" /> Release now
                </Button>
                {m.payout_status === "auto_queued" && (
                  <Button size="sm" variant="outline" data-testid={`hold-payout-${m.match_id}`} onClick={() => holdPayout(m.match_id)} className="rounded-full border-black/20">
                    <PauseCircle className="mr-1 h-3.5 w-3.5" /> Hold
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </>
);

export default PayoutsTab;
